"use client";
import {
  Flex,
  Heading,
  Text,
  Button as ChakraButton,
  Box,
} from "@chakra-ui/react";
import Image from "next/image";
import { Button, Button_T } from "@ui/components/button";
import { colors } from "@areahub/theme";
import { ImageFragment } from "lib/gcms";
import { useAnalytics } from "@areahub/adapters/src/analytics/analytics.client";

export interface EnterpriseHeroProps {
  title: string;

  /**
   * Text above the title
   */
  subtitle: string;
  /**
   * Description text below the title
   */
  pushText: string;
  /**
   * CTA buttons
   */
  buttons: Button_T[];

  image: ImageFragment & { alt?: string };
  bg: string;
  textColor: string;
  titleColor: string;
  subtitleColor: string;
}

export const EnterpriseHero = ({
  title,
  subtitle,
  pushText,
  buttons,
  image,
  bg,
  textColor,
  titleColor,
  subtitleColor,
}: EnterpriseHeroProps) => {
  const scrollToProductPitch = () => {
    const productPitchSection = document.getElementById("pitch-content");
    productPitchSection?.scrollIntoView({ behavior: "smooth" });
  };
  const analytics = useAnalytics();
  return (
    <Box bgColor={bg} overflow="hidden">
      <Flex
        flexDir={{
          base: "column",
          lg: "row",
        }}
        pt={{
          base: 16,
          lg: 24,
        }}
        pb={{
          base: 12,
          lg: 20,
          xl: 12,
        }}
        px={{
          base: 5,
          md: 10,
          xl: 0,
        }}
        overflow="visible"
        maxW="1215px"
        mx="auto"
        justifyContent={"space-between"}
      >
        <Flex
          align={"center"}
          justifyContent={{
            lg: "center",
            xl: "unset",
          }}
          display={{
            base: "none",
            lg: "flex",
          }}
          w={{ lg: "50%" }}
          maxW={{
            lg: image?.width / 2.4,
            xl: image?.width / 1.9,
          }}
        >
          <Image
            {...image}
            loading="eager"
            priority
            src={image?.url as string}
            alt="Mockup of AreaHub Enterprise showcasing environmental risk reporting and climate risk intelligence portfolio monitoring features."
            height={image?.height}
            width={image?.width}
            placeholder={
              typeof image?.blurDataURL === "string" ? "blur" : undefined
            }
            blurDataURL={
              typeof image?.blurDataURL === "string"
                ? image?.blurDataURL
                : undefined
            }
          />
        </Flex>

        <Flex
          flexDir="column"
          w={{
            lg: "50%",
          }}
        >
          <Box
            display={{
              base: "block",
              lg: "none",
            }}
            maxW="350px"
            mx="auto"
          >
            <Image
              {...image}
              loading="eager"
              priority
              alt={image?.alt as string}
              src={image?.url as string}
              placeholder={
                typeof image?.blurDataURL === "string" ? "blur" : undefined
              }
              blurDataURL={
                typeof image?.blurDataURL === "string"
                  ? image?.blurDataURL
                  : undefined
              }
            />
          </Box>

          <Flex flexDir={"row"} alignItems="center">
            <Heading
              as={"h2"}
              fontSize={{
                base: 16,
                md: 18,
                lg: 20,
              }}
              fontWeight="bold"
              color={subtitleColor}
              mb={{
                base: 4,
                lg: 6,
              }}
              mr={1}
              position="relative"
            >
              {subtitle}
            </Heading>
            <Box
              bgColor={subtitleColor}
              flex="1"
              h={{
                base: "2px",
                lg: "3px",
              }}
              mb={{
                base: 4,
                lg: 6,
              }}
            />
          </Flex>
          <Heading
            as={"h1"}
            fontSize={{
              base: 26,
              md: 34,
              lg: 38,
              xl: 45,
            }}
            textColor={titleColor}
            fontWeight={"bold"}
            lineHeight={1.2}
            mb={5}
          >
            {title}
          </Heading>
          <Text
            color={textColor}
            fontSize={{
              base: 14,
              md: 16,
              lg: 17,
              xl: 18,
            }}
            pb={{
              base: 7,
              lg: 9,
            }}
            w={{
              lg: "90%",
              xl: "unset",
            }}
          >
            {pushText}
          </Text>
          <Flex
            flexDir={{
              base: "column",
              lg: "row",
            }}
            alignItems={{
              base: "center",
              lg: "unset",
            }}
            gap={{
              base: 4,
              lg: 5,
            }}
          >
            <Button
              bgColor={colors.enterpriseColors.blue["200"]}
              color={"white"}
              fontSize={{
                base: 12,
                md: 14,
                lg: 16,
                xl: 18,
              }}
              fontWeight={"bold"}
              py={{
                base: 4,
                lg: 7,
                xl: 8,
              }}
              w={{
                base: "140px",
                md: "160px",
                lg: "180px",
                xl: "220px",
              }}
              onClick={() =>
                analytics.track(
                  "User clicked 'Request a Demo' button on enterprise landing page",
                  {
                    ui: "EnterpriseHero",
                  },
                )
              }
              textTransform={"uppercase"}
              {...buttons[0]}
            />
            <ChakraButton
              borderRadius={"full"}
              bgColor={colors.enterpriseColors.blue["500"]}
              color="white"
              fontSize={{
                base: 12,
                md: 14,
                lg: 16,
                xl: 18,
              }}
              fontWeight={"bold"}
              py={{
                base: 4,
                lg: 7,
                xl: 8,
              }}
              w={{
                base: "140px",
                md: "160px",
                lg: "180px",
                xl: "220px",
              }}
              textTransform={"uppercase"}
              onClick={scrollToProductPitch}
            >
              {buttons[1].label}
            </ChakraButton>
          </Flex>
        </Flex>
      </Flex>
    </Box>
  );
};
